/**
 * Created by neo on 16.10.21.
 */
import { AggregationResultEntry, AggregationResultEntryJson, createAggregationResultEntry } from './AggregationResultEntry';
import { makeAutoObservable } from 'mobx';

// Raw JSON from backend
export type ActivityLogAnalyticsAggregationResultJson = {
  id: string;
  activities: AggregationResultEntryJson[];
};

// Observable model type (extends JSON + adds app-specific computed/methods)
export type ActivityLogAnalyticsAggregationResult = Omit<ActivityLogAnalyticsAggregationResultJson, 'activities'> & {
  activities: AggregationResultEntry[];
  readonly total: Record<string, number>;
  readonly totalCount: number;
  readonly date: Date | undefined;
  getTotalCountByExcludingType(types: string[]): number;
  getTotalCountByType(types: string[]): number;
};

// Factory function
export function createActivityLogAnalyticsAggregationResult(
  json?: Partial<ActivityLogAnalyticsAggregationResultJson>
): ActivityLogAnalyticsAggregationResult {
  return makeAutoObservable({
    id: json?.id ?? '',
    activities: (json?.activities ?? []).map((a) => createAggregationResultEntry(a)),

    get total(): Record<string, number> {
      return this.activities.reduce(
        (agg, curr) =>
          Array.from(Object.entries(curr.total)).reduce((total, [key, value]) => {
            if (total[key]) {
              total[key] += value;
            } else {
              total[key] = value;
            }
            return total;
          }, agg),
        {} as Record<string, number>,
      );
    },

    get totalCount(): number {
      return this.activities.reduce((total, curr) => total + curr.count, 0);
    },

    get date(): Date | undefined {
      if (this.id && this.id !== 'all') {
        return new Date(this.id);
      }
      return undefined;
    },

    getTotalCountByExcludingType(types: string[]): number {
      return this.activities.filter(({ type }) => !types.includes(type)).reduce((total, curr) => total + curr.count, 0);
    },

    getTotalCountByType(types: string[]): number {
      return this.activities.filter(({ type }) => types.includes(type)).reduce((total, curr) => total + curr.count, 0);
    },
  });
}
